//@flow
import {IconButton, InputAdornment, makeStyles} from '@material-ui/core';
import React, {useEffect, useState} from 'react';

import type {Theme} from '@material-ui/core';
import CustomTextField from '../CustomTextField/CustomTextField';
import {Send} from '@material-ui/icons';
import TagItem from './TagItem';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    width: '100%',
    height: 'fit-content',
    flexDirection: 'column',
    gap: '.5rem',
  },
  tagList: {
    display: 'flex',
    gap: '.5rem',
    flexWrap: 'wrap',
  },
}));

type TagListItem = {
  label: string,
  value: any,
  id?: string,
};

type TagItemActions = {
  onDelete: (tag: TagListItem) => void,
};

type Props = {
  placeholder?: string,
  label?: string,
  icon?: React$Node,
  onListChange?: (list: TagListItem[]) => void,
  tagList?: TagListItem[],
  onChange?: (value: string) => void,
  value?: string,
  renderTags?: (tag: TagListItem[], actions: TagItemActions) => React$Node,
  onAddTag?: (tag: TagListItem) => void,
};

const MAX_TAG_LENGTH = 40;

export default function TagInput(props: Props) {
  const {
    placeholder,
    label,
    icon,
    onListChange,
    tagList: tagListProp = [],
    onChange,
    value = '',
    renderTags,
    onAddTag,
  } = props;

  const classes = useStyles();

  const [tagList, setTagList] = useState<TagListItem[]>(tagListProp);

  const [inputValue, setInputValue] = useState<string>(value);

  useEffect(() => {
    setTagList(tagListProp);
  }, [tagListProp]);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const addTag = (tag: TagListItem) => {
    const exists = tagList.find(item => item.label === tag.label);

    if (exists) return;

    setTagList(prevState => {
      const newList = [...prevState, tag];
      if (onListChange) onListChange(newList);
      return newList;
    });
  };

  const deleteTag = (tag: TagListItem) => {
    setTagList(prevState => {
      const newList = prevState.filter(item => item.label !== tag.label);
      if (onListChange) onListChange(newList);
      return newList;
    });
  };

  const handleAddTag = () => {
    const value = inputValue.trim();
    if (!value.length === 0) return;
    const tag = {
      label: value,
      value,
    };

    addTag(tag);
    setInputValue('');
    if (onAddTag) onAddTag(tag);
    if (onChange) onChange('');
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddTag();
    }
  };

  return (
    <div className={classes.root}>
      {renderTags ? (
        renderTags(tagList, {
          onDelete: deleteTag,
        })
      ) : (
        <div className={classes.tagList}>
          {tagList.map((tag, index) => (
            <TagItem
              key={index}
              label={tag.label}
              onDelete={() => {
                deleteTag(tag);
              }}
            />
          ))}
        </div>
      )}

      <CustomTextField
        label={label}
        placeholder={placeholder}
        onChange={e => {
          const value = e.target.value;
          if (value.length > MAX_TAG_LENGTH) return;
          setInputValue(value);
          if (onChange) onChange(value);
        }}
        fullWidth
        value={inputValue}
        onKeyDown={handleKeyDown}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="agregar etiqueta"
                onClick={handleAddTag}
                color="primary"
                edge="end"
              >
                {icon ? icon : <Send />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        error={inputValue.length > MAX_TAG_LENGTH}
        helperText={`
         Max ${MAX_TAG_LENGTH} caracteres, ${inputValue.length} de ${MAX_TAG_LENGTH}
        `}
      />
    </div>
  );
}
